/**
 * 403 畫面。靜靜導回會讓人以為網址有效。
 */
export type ForbiddenRole = 'admin' | 'engineer' | 'chatter';

export interface ForbiddenProps {
  /** 想去的頁名（例如「管理」）；只是用來講清楚被擋的是哪一頁。 */
  page: string;
  /**
   * `section`：整區進不去（房間或系統）。
   * `admin-tab`：工程師進得了系統，但這一頁只給管理者。
   */
  reason?: 'section' | 'admin-tab';
  loginRole: ForbiddenRole;
  /** 回工作台。 */
  onBack: () => void;
}

export function forbiddenDetail(loginRole: ForbiddenRole, reason: 'section' | 'admin-tab'): string {
  switch (reason) {
    case 'admin-tab':
      return adminTabDetail(loginRole);
    case 'section':
      return sectionDetail(loginRole);
    default: {
      const neverReason: never = reason;
      return neverReason;
    }
  }
}

function adminTabDetail(loginRole: ForbiddenRole): string {
  switch (loginRole) {
    case 'engineer':
      return '你的登入角色是 engineer，可以進系統裡這個角色看得到的頁，例如監控與運行參數。這一頁只給管理者，不是整個系統都進不去。';
    case 'admin':
      return '你的登入角色是 admin。這一頁仍不開放，請回工作台。';
    case 'chatter':
      return sectionDetail('chatter');
    default: {
      const neverRole: never = loginRole;
      return neverRole;
    }
  }
}

function sectionDetail(loginRole: ForbiddenRole): string {
  switch (loginRole) {
    case 'engineer':
      return '你的登入角色是 engineer：可以在收件匣回訊息、在帳號頁看自己的身分，並進系統裡這個角色看得到的頁。房間與 sugargirl 只給管理者。';
    case 'admin':
      return '你的登入角色是 admin。這一頁仍被擋住，請回工作台。';
    case 'chatter':
      return '你的帳號是聊手身分：可以在收件匣回訊息、在帳號頁看自己的身分。總覽、工作階段、任務與系統這些系統管理頁不在你的範圍；需要的話請找管理員。';
    default: {
      const neverRole: never = loginRole;
      return neverRole;
    }
  }
}

export function Forbidden({ page, onBack, reason = 'section', loginRole }: ForbiddenProps) {
  const adminTab = reason === 'admin-tab';
  return (
    <div
      className="flex min-h-0 flex-1 items-start justify-center px-6 pt-16"
      data-testid="forbidden"
      data-reason={reason}
      data-login-role={loginRole}
    >
      <div className="w-full max-w-md">
        <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">403 · 沒有權限</p>
        <h2 className="mt-2 text-lg font-semibold text-text">
          {adminTab ? `「${page}」需要管理者` : `「${page}」只有系統管理員看得到`}
        </h2>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{forbiddenDetail(loginRole, reason)}</p>
        <button
          type="button"
          onClick={onBack}
          className="mt-5 inline-flex h-9 items-center rounded-md bg-accent px-4 text-sm font-medium text-accent-foreground hover:opacity-90"
          data-testid="forbidden-back"
        >
          回收件匣
        </button>
      </div>
    </div>
  );
}
